import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionCard from '@/components/feature/SectionCard';
import TeamBadge from '@/components/base/TeamBadge';
import { useSEO } from '@/hooks/useSEO';
import { getPlayer, getTeam } from '@/utils/data';

export default function PlayerDetailPage() {
  const { playerId } = useParams();
  const player = getPlayer(playerId);
  const team = getTeam(player?.teamId);

  useSEO({
    title: player ? `${player.name} 数据分析 - 篮球比分` : '球员不存在 - 篮球比分',
    description: player ? `${player.name}的个人资料、本赛季数据统计与近期表现趋势分析。` : '未找到对应的球员信息。',
    keywords: player ? `${player.name},球员数据,篮球球员,${player.position}` : '篮球球员',
  });

  if (!player || !team) {
    return (
      <div className="px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-user-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-900">球员不存在</h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-md">未找到该球员的资料，可能已被移除或链接有误。</p>
        <Link to="/players" className="mt-6 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer whitespace-nowrap">
          浏览全部球员
        </Link>
      </div>
    );
  }

  const seasonStats = [
    { label: '场均得分', value: player.ppg, unit: '分' },
    { label: '场均篮板', value: player.rpg, unit: '个' },
    { label: '场均助攻', value: player.apg, unit: '次' },
    { label: '场均抢断', value: player.spg, unit: '次' },
    { label: '场均盖帽', value: player.bpg, unit: '次' },
    { label: '投篮命中率', value: player.fg, unit: '' },
  ];

  const maxTrend = Math.max(...player.trend.map((t) => t.value), 1);

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '球员', to: '/players' },
          { label: player.name },
        ]}
      />

      <section className="mt-4 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
        <div className="flex flex-col sm:flex-row gap-5">
          <div
            className="w-24 h-24 md:w-28 md:h-28 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: team.color }}
          >
            <span className="font-heading font-extrabold text-4xl md:text-5xl text-white">{player.number}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{player.name}</h1>
              <span className="text-sm text-foreground-500">{player.enName}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs md:text-sm">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700">{player.position}</span>
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800">{player.country}</span>
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900">{player.age} 岁</span>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded-md bg-background-100 px-3 py-2">
                <span className="block text-xs text-foreground-500">身高</span>
                <span className="font-medium text-foreground-900">{player.height}</span>
              </div>
              <div className="rounded-md bg-background-100 px-3 py-2">
                <span className="block text-xs text-foreground-500">体重</span>
                <span className="font-medium text-foreground-900">{player.weight}</span>
              </div>
              <div className="rounded-md bg-background-100 px-3 py-2">
                <span className="block text-xs text-foreground-500">球衣号码</span>
                <span className="font-medium text-foreground-900">{player.number} 号</span>
              </div>
              <div className="rounded-md bg-background-100 px-3 py-2">
                <span className="block text-xs text-foreground-500">出场场次</span>
                <span className="font-medium text-foreground-900">{player.gamesPlayed} 场</span>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed max-w-4xl">{player.intro}</p>
      </section>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="本赛季数据" icon="ri-bar-chart-grouped-line">
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              {seasonStats.map((s) => (
                <div key={s.label} className="rounded-md bg-background-100/70 border border-background-200/60 px-4 py-4">
                  <span className="block font-heading font-extrabold text-2xl text-foreground-950">
                    {s.value}
                    {s.unit && <span className="text-sm font-medium text-foreground-500 ml-1">{s.unit}</span>}
                  </span>
                  <span className="block text-xs text-foreground-500 mt-1">{s.label}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="近期表现趋势" icon="ri-line-chart-line">
            <div className="px-4 md:px-5 py-5">
              <div className="flex items-end justify-between gap-2 md:gap-3 h-44">
                {player.trend.map((t) => {
                  const h = Math.round((t.value / maxTrend) * 100);
                  return (
                    <div key={t.label} className="flex-1 flex flex-col items-center justify-end h-full gap-2">
                      <span className="text-xs font-heading font-bold text-foreground-800 tabular-nums">{t.value}</span>
                      <div className="w-full max-w-[42px] rounded-t-md bg-gradient-to-t from-primary-500 to-primary-400 transition-all" style={{ height: `${h}%` }}></div>
                      <span className="text-[10px] md:text-xs text-foreground-500 whitespace-nowrap">{t.label}</span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-foreground-500 text-center">近 7 场比赛得分走势（演示数据）</p>
            </div>
          </SectionCard>
        </div>

        <aside className="space-y-6">
          <SectionCard title="所属球队" icon="ri-team-line">
            <div className="p-4">
              <Link
                to={`/team/${team.id}`}
                className="flex items-center gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
              >
                <TeamBadge team={team} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{team.name}</p>
                  <p className="text-xs text-foreground-500 truncate">{team.leagueName} · 排名第 {team.rank} 位</p>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300"></i>
              </Link>
              <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
                主场：{team.arena}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="数据摘要" icon="ri-file-list-line">
            <ul className="px-4 py-4 space-y-2.5 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">场均得分排名</span>
                <span className="font-medium text-foreground-900">联盟前列</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">出场场次</span>
                <span className="font-medium text-foreground-900">{player.gamesPlayed} 场</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">投篮命中率</span>
                <span className="font-medium text-foreground-900">{player.fg}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-500">位置</span>
                <span className="font-medium text-foreground-900">{player.position}</span>
              </li>
            </ul>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}